import type { OptionsDocument } from 'paintscout'
// Deep import, not the '@paintscout/util/builder' barrel. That barrel carries a large web of
// circular imports, and pulling it into this leaf module broke the Vite dev graph with
// "doesn't provide an export named: 'getDunningStatusMessage'".
import { getObjectLabels } from '@paintscout/util/builder/util/get-object-labels'

export type DunningStatus = 'restricted' | 'locked'

// Shared copy for the payment-failure dunning gate (see #21609) — used by both DropdownNewButton's
// tooltip and Header's banner so the two surfaces can't drift out of sync. The server enforces the
// actual gate (assertQuoteActionAllowed.ts) regardless of what's shown here.
//
// `options` is optional so the copy still reads correctly before client options have loaded, in
// which case getObjectLabels falls back to the stock 'Quote'/'Invoice' terminology.
export function getDunningStatusMessage({
  status,
  options
}: {
  status: DunningStatus
  options?: OptionsDocument
}): string {
  if (status === 'locked') {
    return 'This account is locked due to a failed payment.'
  }

  // Both labels live under the `quote` key — `invoice: true` switches getObjectLabels to the
  // invoice terminology rather than the estimate terminology (same trick as archiveDealCopy.ts).
  // So an account that renames Estimate -> Proposal or Invoice -> Bill sees its own words.
  const quotes = getObjectLabels({ options }).quote.plural.toLowerCase()
  const invoices = getObjectLabels({ options, invoice: true }).quote.plural.toLowerCase()

  return `This account is restricted due to a failed payment — new ${quotes} and ${invoices} can’t be created or sent.`
}
